// Courses strings, backed by the `courses` i18n namespace; PT fallbacks keep node tests and pre-init renders working.
import { tr } from "../i18nText.js"
import type { CourseKey, ReadinessBand, ReviewLevel } from "./types.js"

const c = (key: string, fallback: string, options?: Record<string, unknown>) => tr(`courses:${key}`, fallback, options)

export const COURSE_TITLES: Record<CourseKey, string> = {
  get "adopter-dog"() { return c("titles.adopterDog", "Curso do Adotante (Cão)") },
  get "adopter-cat"() { return c("titles.adopterCat", "Curso do Adotante (Gato)") },
  get "publisher-training"() { return c("titles.publisherTraining", "Formação de Anunciante") },
}

export const S = {
  common: {
    get next() { return c("common.next", "Seguinte") },
    get back() { return c("common.back", "Voltar") },
    get close() { return c("common.close", "Fechar") },
    get loading() { return c("common.loading", "A carregar...") },
    get retry() { return c("common.retry", "Tentar outra vez") },
    get check() { return c("common.check", "Verificar") },
    get genericError() { return c("common.genericError", "Algo correu mal. Tenta novamente.") },
    get loginRequired() { return c("common.loginRequired", "Precisas de ter sessão iniciada para continuar.") },
    minutes: (n: number) => c("common.minutes", `${n} min`, { n }),
  },
  leave: {
    get title() { return c("leave.title", "Sair a meio?") },
    get message() { return c("leave.message", "O teu progresso fica guardado. Podes continuar mais tarde, noutro dispositivo.") },
    get confirm() { return c("leave.confirm", "Guardar e sair") },
    get cancel() { return c("leave.cancel", "Continuar") },
    get formMessage() { return c("leave.formMessage", "Se saíres agora, as respostas que ainda não enviaste não ficam guardadas.") },
    get formConfirm() { return c("leave.formConfirm", "Sair") },
  },
  player: {
    get sections() { return c("player.sections", "Secções") },
    get required() { return c("player.required", "Obrigatória") },
    get optional() { return c("player.optional", "Opcional") },
    get done() { return c("player.done", "Concluída") },
    get needsRetake() { return c("player.needsRetake", "Atualizada — rever") },
    get start() { return c("player.start", "Começar") },
    get resume() { return c("player.resume", "Continuar") },
    get review() { return c("player.review", "Rever") },
    progressLabel: (percent: number) => c("player.progressLabel", `${percent}% concluído`, { percent }),
    stepOf: (i: number, n: number) => c("player.stepOf", `Passo ${i} de ${n}`, { i, n }),
    get finalTestTitle() { return c("player.finalTestTitle", "Teste final") },
    get finalTestLocked() { return c("player.finalTestLocked", "Conclui as secções obrigatórias para desbloquear o teste final.") },
    finalTestIntro: (count: number, pass: number) =>
      c(
        "player.finalTestIntro",
        `${count} perguntas. Precisas de ${pass}% para concluir o curso. Podes repetir sempre que quiseres.`,
        { count, pass },
      ),
    get startFinalTest() { return c("player.startFinalTest", "Começar teste") },
    get completedTitle() { return c("player.completedTitle", "Curso concluído") },
    get completedBody() { return c("player.completedBody", "Obrigado pelo teu tempo. Isto faz mesmo diferença para os animais.") },
    get backToAnimal() { return c("player.backToAnimal", "Voltar ao animal") },
    get backHome() { return c("player.backHome", "Ir para o início") },
  },
  checks: {
    get chooseOne() { return c("checks.chooseOne", "Escolhe uma opção") },
    get chooseMany() { return c("checks.chooseMany", "Escolhe todas as opções corretas") },
    get trueLabel() { return c("checks.trueLabel", "Verdadeiro") },
    get falseLabel() { return c("checks.falseLabel", "Falso") },
    statementIs: (value: boolean) =>
      value
        ? c("checks.statementTrue", "A afirmação é verdadeira.")
        : c("checks.statementFalse", "A afirmação é falsa."),
    get matchingHelp() { return c("checks.matchingHelp", "Toca num item de cada coluna para os ligar. Toca num par para o desfazer.") },
    matchingCount: (done: number, total: number) => c("checks.matchingCount", `${done}/${total} ligados`, { done, total }),
    get correct() { return c("checks.correct", "Certo!") },
    get wrong() { return c("checks.wrong", "Ainda não.") },
    get correctAnswers() { return c("checks.correctAnswers", "Resposta certa:") },
    get correctPairs() { return c("checks.correctPairs", "Pares certos:") },
    get why() { return c("checks.why", "Porquê") },
    get tryAgainHint() { return c("checks.tryAgainHint", "Vais ver a pergunta outra vez com opções diferentes.") },
    get leftColumn() { return c("checks.leftColumn", "Coluna A") },
    get rightColumn() { return c("checks.rightColumn", "Coluna B") },
    pairNumber: (n: number) => c("checks.pairNumber", `Par ${n}`, { n }),
  },
  finalTest: {
    progress: (i: number, n: number) => c("finalTest.progress", `Pergunta ${i} de ${n}`, { i, n }),
    get finish() { return c("finalTest.finish", "Terminar teste") },
    get passedTitle() { return c("finalTest.passedTitle", "Passaste!") },
    passedBody: (percent: number) => c("finalTest.passedBody", `Tiveste ${percent}%.`, { percent }),
    get failedTitle() { return c("finalTest.failedTitle", "Quase lá") },
    failedBody: (percent: number, pass: number) =>
      c("finalTest.failedBody", `Tiveste ${percent}%. Precisas de ${pass}%.`, { percent, pass }),
    get reviewSections() { return c("finalTest.reviewSections", "Vale a pena rever:") },
    get retake() { return c("finalTest.retake", "Repetir teste") },
  },
  gates: {
    get adopterTitle() { return c("gates.adopterTitle", "Antes de mostrares interesse") },
    adopterBody: (courseTitle: string) =>
      c(
        "gates.adopterBody",
        `Quem publicou este animal pede que vejas primeiro um curso curto: ${courseTitle}. Ajuda-te a preparar a chegada do animal e mostra que levas a adoção a sério.`,
        { courseTitle },
      ),
    adopterEstimate: (minutes: number) =>
      c("gates.adopterEstimate", `Cerca de ${minutes} minutos. O progresso fica guardado.`, { minutes }),
    get adopterCta() { return c("gates.adopterCta", "Ver o curso") },
    get adopterLater() { return c("gates.adopterLater", "Agora não") },
    get publisherTitle() { return c("gates.publisherTitle", "Falta a Formação de Anunciante") },
    get publisherBody() {
      return c(
        "gates.publisherBody",
        "Antes de publicares novos animais, faz a formação curta da Pawseum. Os animais que já publicaste continuam visíveis e editáveis.",
      )
    },
    get publisherCta() { return c("gates.publisherCta", "Fazer a formação") },
    get publisherBanner() { return c("gates.publisherBanner", "Conclui a Formação de Anunciante para poderes publicar novos animais.") },
    get applyNoticeTitle() { return c("gates.applyNoticeTitle", "Fazes uma formação curta") },
    get applyNoticeBody() {
      return c("gates.applyNoticeBody", "Depois da aprovação, fazes a Formação de Anunciante antes de publicar o primeiro animal.")
    },
  },
  form: {
    get title() { return c("form.title", "Candidatura à adoção") },
    get consentPawseum() {
      return c(
        "form.consentPawseum",
        "Percebo que as minhas respostas e um resumo automático das mesmas são partilhados com quem publicou o animal.",
      )
    },
    get consentOwn() { return c("form.consentOwn", "Percebo que as minhas respostas são partilhadas com quem publicou o animal.") },
    get privacyLink() { return c("form.privacyLink", "Política de privacidade") },
    get returningTitle() { return c("form.returningTitle", "Já respondeste ao Formulário de Adoção Pawseum") },
    get returningBody() { return c("form.returningBody", "Podes reutilizar as tuas respostas anteriores ou revê-las antes de enviar.") },
    get viewAnswers() { return c("form.viewAnswers", "ver respostas") },
    get hideAnswers() { return c("form.hideAnswers", "esconder respostas") },
    get editAnswers() { return c("form.editAnswers", "Rever respostas") },
    get confirmSend() { return c("form.confirmSend", "Confirmar e enviar") },
    get submit() { return c("form.submit", "Enviar candidatura") },
    get submitting() { return c("form.submitting", "A enviar...") },
    get doneTitle() { return c("form.doneTitle", "Candidatura enviada!") },
    get doneBody() { return c("form.doneBody", "Quem publicou o animal vai analisar a tua candidatura e entrar em contacto contigo.") },
    get myApplications() { return c("form.myApplications", "Ver as minhas candidaturas") },
    get noForm() { return c("form.noForm", "Este animal não tem formulário de candidatura.") },
  },
  band: {
    get title() { return c("band.title", "Análise automática") },
    get pending() { return c("band.pending", "Análise em curso") },
    get pendingBody() { return c("band.pendingBody", "A análise automática ainda não terminou. As respostas já estão disponíveis abaixo.") },
    get unavailable() { return c("band.unavailable", "Análise indisponível") },
    get unavailableBody() { return c("band.unavailableBody", "Não foi possível analisar esta candidatura. Vê as respostas abaixo.") },
    get notGraded() { return c("band.notGraded", "Formulário próprio — sem análise automática") },
    get disclaimer() { return c("band.disclaimer", "Sugestão automática. A decisão é sempre tua.") },
    courseCompleted: (species: "dog" | "cat") =>
      species === "dog"
        ? c("band.courseCompletedDog", "Curso do Adotante (Cão) concluído")
        : c("band.courseCompletedCat", "Curso do Adotante (Gato) concluído"),
  },
  prompt: {
    get title() { return c("prompt.title", "Estás mesmo a pensar adotar?") },
    get body() {
      return c(
        "prompt.body",
        "Temos um curso curto sobre adoção responsável. Vai ser pedido para mostrares interesse em animais de anunciantes particulares, por isso podes adiantá-lo já.",
      )
    },
    get dog() { return c("prompt.dog", "Curso para cães") },
    get cat() { return c("prompt.cat", "Curso para gatos") },
    get dismiss() { return c("prompt.dismiss", "Agora não") },
  },
  applications: {
    get title() { return c("applications.title", "Candidaturas") },
    get empty() { return c("applications.empty", "Ainda não recebeste candidaturas.") },
    get candidate() { return c("applications.candidate", "Candidato") },
    get answers() { return c("applications.answers", "Respostas") },
    get openChat() { return c("applications.openChat", "Chat") },
    submittedOn: (date: string) => c("applications.submittedOn", `Submetida a ${date}`, { date }),
    get notFound() { return c("applications.notFound", "Candidatura não encontrada.") },
    get loadError() { return c("applications.loadError", "Erro ao carregar candidaturas.") },
  },
  callout: {
    get info() { return c("callout.info", "Informação") },
    get warning() { return c("callout.warning", "Atenção") },
    get tip() { return c("callout.tip", "Dica") },
  },
  devPreview: {
    get course() { return c("devPreview.course", "Curso") },
    get source() { return c("devPreview.source", "Conteúdo") },
    get sourceReal() { return c("devPreview.sourceReal", "Real (API)") },
    get sourceFixtures() { return c("devPreview.sourceFixtures", "Fixtures") },
    get reset() { return c("devPreview.reset", "Recomeçar curso") },
    get resetting() { return c("devPreview.resetting", "A recomeçar…") },
    get resetDone() { return c("devPreview.resetDone", "Progresso apagado. O curso recomeça do início.") },
    get resetMissing() {
      return c("devPreview.resetMissing", "O backend ainda não tem o endpoint DEV de reset (POST /api/dev/courses/:courseKey/reset).")
    },
    get resetFailed() { return c("devPreview.resetFailed", "Não foi possível recomeçar o curso.") },
    get viewAll() { return c("devPreview.viewAll", "Ver tudo") },
    get viewAllHint() {
      return c("devPreview.viewAllHint", "Abre a Revisão de conteúdo: todas as secções e passos, incluindo as respostas certas (só admins).")
    },
    get backToPreview() { return c("devPreview.backToPreview", "Voltar ao preview") },
  },
}

const BAND_FALLBACK: Record<ReadinessBand, { label: string; guidance: string }> = {
  RISCO_ELEVADO: {
    label: "Risco elevado",
    guidance: "Provavelmente não é a combinação certa neste momento. Considera recusar com simpatia.",
  },
  CAUTELA: { label: "Cautela", guidance: "Há algumas lacunas. Faz mais perguntas antes de avançar." },
  BOM: { label: "Bom", guidance: "Boas respostas. Conversa com a pessoa para a conheceres melhor." },
  EXCELENTE: { label: "Suspeitamente bom", guidance: "Ou é um adotante fantástico, ou uma IA. Confirma pessoalmente." },
}

export function bandCopy(band: ReadinessBand): { label: string; guidance: string } {
  return {
    label: c(`band.bands.${band}.label`, BAND_FALLBACK[band].label),
    guidance: c(`band.bands.${band}.guidance`, BAND_FALLBACK[band].guidance),
  }
}

const LEVEL_FALLBACK: Record<ReviewLevel, string> = {
  STRONG: "Resposta forte",
  OK: "Resposta razoável",
  WEAK: "Resposta fraca",
  UNCERTAIN: "Inconclusivo",
}

export function levelCopy(level: ReviewLevel): string {
  return c(`band.levels.${level}`, LEVEL_FALLBACK[level])
}
